import type { WidgetFC, WidgetProps } from '../../common/Widget/types';
import React, { useContext } from 'react';
import MessageListForSearch from '../../messages/MessageListForSearch/MessageListForSearch';
import { useClassNameMapper } from 'react-bootstrap';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import localStyles from './MessageListForSearchWidget.module.pcss';
import type { ListVariantTypeAndProps } from '@aurora/shared-client/components/common/List';
import { ListVariant } from '@aurora/shared-client/components/common/List/enums';
import { PagerVariant } from '@aurora/shared-client/components/common/Pager/enums';
import { IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import type {
  PagerInfiniteScrollTypeAndProps,
  PagerLoadMoreTypeAndProps
} from '@aurora/shared-client/components/common/Pager/types';
import type {
  ContextUserQuery,
  ContextUserQueryVariables,
  MessageSearchViewFragment
} from '../../../types/graphql-types';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import userQuery from '../../context/ContextUser.query.graphql';
import { useMeasure } from 'react-use';
import type { ItemViewVariantProps } from '../../entities/types';
import type { ItemType, MessageViewVariant } from '../../../types/enums';
import { Breakpoint } from '../../../types/enums';
import { EndUserQueryParams } from '@aurora/shared-types/pages/enums';
import type {
  ContextNodeQuery,
  ContextNodeQueryVariables
} from '@aurora/shared-generated/types/graphql-types';
import nodeQuery from '@aurora/shared-client/components/context/ContextNode.query.graphql';
import { PanelType } from '@aurora/shared-client/components/common/Panel/enums';
import EditContext from '../../context/EditContext/EditContext';

interface Props extends WidgetProps {
  /**
   * Class name(s) to apply to the wrapping element.
   */
  className?: string;
}

/**
 * Renders messages in search result page
 *
 * @author Kiran Kumari
 */
const MessageListForSearchWidget: WidgetFC<Props> = ({ className }) => {
  const cx = useClassNameMapper(localStyles);
  const [reference, { width }] = useMeasure();
  const isDesktopBreakpoint = width > Breakpoint.SM || width === 0; // width is initially 0 on SSR
  const messageListPageSize = isDesktopBreakpoint ? 10 : 5;
  const messageListLoadMorePageSize = messageListPageSize;
  const { router } = useEndUserRoutes();
  const { showEditControls: showPageEditControls } = useContext(EditContext);
  const lastUpdateFilter = router.getUnwrappedQueryParam(
    EndUserQueryParams.SEARCH_FILTER_BY_LAST_UPDATE
  );
  const authorFilter = router.getUnwrappedQueryParam(EndUserQueryParams.SEARCH_FILTER_BY_AUTHOR);
  const queryTerm = router.getUnwrappedQueryParam(EndUserQueryParams.SEARCH_KEY);
  const sectionWrapperCss: string = cx('lia-content-section lia-g-mb-lg-20');
  const listVariant: ListVariantTypeAndProps<MessageSearchViewFragment> = {
    type: ListVariant.LIST_GROUP,
    props: {
      listItemClassName: (): string => cx('lia-search-item lia-message-search-item')
    }
  };

  const messageViewVariantPropsOverrides: ItemViewVariantProps<
    ItemType.MESSAGE_SEARCH,
    MessageViewVariant.INLINE
  > = {
    className: cx('lia-content-box'),
    boardIconSize: IconSize.PX_24,
    useSolvedBadge: true,
    clampBodyLines: 2,
    useRepliesCount: true,
    useViewCount: true,
    useAttachments: true,
    useKudosCount: true,
    useUserHoverCard: false,
    useNodeHoverCard: false,
    useTeaser: false,
    useLastUpdateDateText: !!lastUpdateFilter,
    useSearchSnippet: true,
    useMedia: false,
    usePreviewMedia: false,
    useVideoPreview: false
  };

  const messageListInfiniteScrollPagerVariant: PagerInfiniteScrollTypeAndProps = {
    type: PagerVariant.INFINITE_SCROLL
  };

  const messageListLoadMorePagerVariant: PagerLoadMoreTypeAndProps = {
    type: PagerVariant.LOAD_MORE
  };

  const locationFilter = router.getUnwrappedQueryParam(
    EndUserQueryParams.SEARCH_FILTER_BY_LOCATION
  );

  const { data } = useQueryWithTracing<ContextNodeQuery, ContextNodeQueryVariables>(
    module,
    nodeQuery,
    {
      variables: {
        id: locationFilter
      },
      skip: !locationFilter
    }
  );

  const { data: contextUserData } = useQueryWithTracing<
    ContextUserQuery,
    ContextUserQueryVariables
  >(module, userQuery, {
    variables: {
      id: `user:${authorFilter}`
    },
    skip: !authorFilter
  });

  return (
    <div className={cx('lia-content')} ref={reference}>
      <section className={sectionWrapperCss}>
        <MessageListForSearch
          paneledListClassname={cx('lia-message-content-list', className)}
          footerClassname={cx('lia-messageList-footer')}
          queryTerm={queryTerm}
          listVariant={listVariant}
          contextNode={data?.coreNode}
          author={contextUserData?.user}
          messageViewVariantPropsOverrides={messageViewVariantPropsOverrides}
          pagerVariant={
            isDesktopBreakpoint && !showPageEditControls
              ? messageListInfiniteScrollPagerVariant
              : messageListLoadMorePagerVariant
          }
          panel={PanelType.DIVIDER}
          useEmpty
          useLoader
          pageSize={messageListPageSize}
          loadMorePageSize={messageListLoadMorePageSize}
          useAdditionalConstraintsAndSorts
        />
      </section>
    </div>
  );
};

export default MessageListForSearchWidget;
